import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import { logsAPI, type LogEntry } from '../services/api';
import { Button, Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { FileText, Trash2, RefreshCw, Search, Play, Pause, Download, X, Check } from 'lucide-react';

const LogsPage: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState(1000);
  const [searchInput, setSearchInput] = useState(''); // Input value (updates immediately)
  const [searchTerm, setSearchTerm] = useState(''); // Actual search term (updates with debounce)
  const [totalLines, setTotalLines] = useState<number | null>(null);
  const [retrievedLines, setRetrievedLines] = useState(0);
  const [scanTruncated, setScanTruncated] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [logFiles, setLogFiles] = useState<Array<{
    name: string;
    path: string;
    size: number;
    size_formatted: string;
    modified_date: string;
    modified_date_formatted: string;
  }>>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { lines };
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      const data = await logsAPI.getLogs(params);
      setLogs(data.logs);
      setTotalLines(data.total_lines);
      setRetrievedLines(data.retrieved_lines);
      setScanTruncated(data.scan_truncated);
    } catch (err: any) {
      console.error('Failed to load logs:', err);
      // Show error message to user
      if (err.response?.data?.error) {
        alert(`Error loading logs: ${err.response.data.error}\n\n${err.response.data.message || ''}`);
      } else {
        alert(`Error loading logs: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [lines, searchTerm]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Prevent duplicate logs loading on initial mount
  const logsLoadedRef = React.useRef(false);

  useEffect(() => {
    if (user && user.can_view_all_tasks && !logsLoadedRef.current) {
      logsLoadedRef.current = true;
      loadLogs();
    }
  }, [user, loadLogs]);

  // Reload logs when searchTerm or lines change (after initial load)
  useEffect(() => {
    if (user && user.can_view_all_tasks && logsLoadedRef.current) {
      loadLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, lines]);

  useEffect(() => {
    if (autoRefresh && user && user.can_view_all_tasks) {
      const interval = setInterval(() => {
        loadLogs();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, user, loadLogs]);

  const handleOpenDownloadModal = async () => {
    try {
      setLoadingFiles(true);
      const data = await logsAPI.getLogFiles();
      setLogFiles(data.files);
      setDownloadModalOpen(true);
    } catch (err: any) {
      console.error('Failed to load log files:', err);
      alert('Failed to load log files: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleToggleFileSelection = (fileName: string) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileName)) {
      newSelected.delete(fileName);
    } else {
      newSelected.add(fileName);
    }
    setSelectedFiles(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedFiles.size === logFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(logFiles.map(f => f.name)));
    }
  };

  const handleDownload = async () => {
    if (selectedFiles.size === 0) {
      alert('Please select at least one file to download');
      return;
    }

    try {
      setDownloading(true);
      const fileNames = Array.from(selectedFiles);
      const blob = await logsAPI.downloadLogFiles(fileNames);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileNames.length === 1 ? fileNames[0] : 'log_files.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setDownloadModalOpen(false);
      setSelectedFiles(new Set());
    } catch (err: any) {
      console.error('Failed to download log files:', err);
      alert('Failed to download log files: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setDownloading(false);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all logs? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await logsAPI.clearLogs();
      if (result.success) {
        loadLogs();
        alert('Logs cleared successfully');
      } else {
        alert(result.error || 'Failed to clear logs');
      }
    } catch (err: any) {
      console.error('Failed to clear logs:', err);
      alert('Failed to clear logs');
    }
  };

  if (!user || !user.can_view_all_tasks) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Access Denied</h3>
            <p className="mt-1 text-sm text-gray-600">You do not have permission to view this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-50 rounded-lg">
                <FileText className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">System Logs</h1>
                <p className="mt-1 text-sm text-gray-600">View system logs and API activity</p>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lines to load
                </label>
                <select
                  value={lines}
                  onChange={(e) => setLines(Number(e.target.value))}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                >
                  <option value={100}>Last 100</option>
                  <option value={500}>Last 500</option>
                  <option value={1000}>Last 1000</option>
                  <option value={5000}>Last 5000</option>
                  <option value={10000}>Last 10000</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setSearchTerm(searchInput.trim());
                      }
                    }}
                    placeholder="Search in logs..."
                    className="w-full pl-10 pr-4 rounded-lg border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                  />
                </div>
              </div>

              <div className="flex items-end">
                <Button
                  onPress={loadLogs}
                  isDisabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  {loading ? 'Loading...' : 'Refresh'}
                </Button>
              </div>

              <div className="flex items-end gap-2">
                <Button
                  onPress={handleOpenDownloadModal}
                  isDisabled={loadingFiles}
                  className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
                <Button
                  onPress={() => setAutoRefresh(!autoRefresh)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg font-medium transition-colors ${
                    autoRefresh
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  }`}
                >
                  {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  {autoRefresh ? 'Auto' : 'Manual'}
                </Button>
                <Button
                  onPress={handleClearLogs}
                  className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear
                </Button>
              </div>
            </div>

            {/* Stats */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex flex-wrap justify-between gap-4 text-sm text-gray-600">
                <span>
                  Total lines in log:{' '}
                  <strong>{totalLines === null ? 'not counted (bounded read)' : totalLines.toLocaleString()}</strong>
                </span>
                <span>Retrieved: <strong>{retrievedLines.toLocaleString()}</strong></span>
                {searchTerm && <span>Filtered by: <strong>"{searchTerm}"</strong></span>}
                {scanTruncated && (
                  <span className="text-amber-700">
                    Scan limit reached; older log entries or matches may exist
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Logs display */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-900 text-gray-100 p-4 font-mono text-sm overflow-x-auto max-h-[600px] overflow-y-auto">
              {logs.length === 0 ? (
                <div className="text-gray-400 italic">
                  <div>No logs to display</div>
                  <div className="mt-2 text-xs text-gray-500">
                    Note: On Railway and other ephemeral file systems, logs may only be available in stdout/stderr.
                    <br />
                    Check the deployment logs in the Railway dashboard for real-time logs.
                  </div>
                </div>
              ) : (
                logs.map((log, index) => (
                  <div
                    key={index}
                    className={`py-1 ${
                      log.content.includes('ERROR') || log.content.includes('CRITICAL')
                        ? 'text-red-400'
                        : log.content.includes('WARNING')
                        ? 'text-yellow-400'
                        : log.content.includes('INFO')
                        ? 'text-accent-400'
                        : log.content.includes('DEBUG')
                        ? 'text-gray-500'
                        : 'text-gray-300'
                    }`}
                  >
                    {log.content}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Download Modal */}
      {downloadModalOpen && (
        <ModalOverlay
          isOpen={downloadModalOpen}
          onOpenChange={setDownloadModalOpen}
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
        >
          <Modal className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <Dialog className="flex flex-col h-full min-h-0 outline-none">
              <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200">
                <Heading className="text-xl font-semibold text-gray-900">Download Log Files</Heading>
                <Button
                  onPress={() => {
                    setDownloadModalOpen(false);
                    setSelectedFiles(new Set());
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-6">
                {loadingFiles ? (
                  <div className="text-center py-8 text-gray-600">Loading log files...</div>
                ) : logFiles.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">No log files found</div>
                ) : (
                  <>
                    <div className="mb-4 flex items-center justify-between">
                      <Button
                        onPress={handleSelectAll}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {selectedFiles.size === logFiles.length ? 'Deselect All' : 'Select All'}
                      </Button>
                      <span className="text-sm text-gray-600">
                        {selectedFiles.size} of {logFiles.length} selected
                      </span>
                    </div>
                    <div className="space-y-2">
                      {logFiles.map((file) => {
                        const isSelected = selectedFiles.has(file.name);
                        return (
                          <div
                            key={file.name}
                            onClick={() => handleToggleFileSelection(file.name)}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected
                                ? 'border-blue-500 bg-blue-500'
                                : 'border-gray-300'
                            }`}>
                              {isSelected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">{file.name}</div>
                              <div className="text-sm text-gray-500">
                                {file.size_formatted} • {file.modified_date_formatted}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-white flex items-center justify-end gap-3">
                <Button
                  onPress={() => {
                    setDownloadModalOpen(false);
                    setSelectedFiles(new Set());
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </Button>
                <Button
                  onPress={handleDownload}
                  isDisabled={downloading || selectedFiles.size === 0}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloading ? 'Downloading...' : `Download ${selectedFiles.size > 0 ? `(${selectedFiles.size})` : ''}`}
                </Button>
              </div>
            </Dialog>
          </Modal>
        </ModalOverlay>
      )}
    </div>
  );
};

export default LogsPage;
